import { apiErrorMessage } from '../lib/api-error-message';
/**
 * CustomerDetail Page - T071
 */
import { useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { ProtectedRoute } from '../components/ProtectedRoute';
import { AppShell } from '../components/layout/AppShell';
import {
  GlassPanel,
  Button,
  Modal,
  LoadingSpinner,
  PageHeader,
  StatusBadge,
} from '../components/ui';
import { CustomerForm } from '../components/customers/CustomerForm';
import { AddressForm } from '../components/customers/AddressForm';
import { EntityAuditSection } from '../components/audit/EntityAuditSection';
import {
  useCustomer,
  useUpdateCustomer,
  useArchiveCustomer,
  useCustomerAddresses,
  useCreateAddress,
  useDeleteAddress,
} from '../hooks/useCustomers';
import { useToastHelpers } from '../components/ui/Toast';
import { formatPaymentTerm } from '@armard/shared';
import { markFormSaved } from '../hooks/useUnsavedChanges';

export const Route = createFileRoute('/customers_/$id')({
  component: CustomerDetailPage,
});

function CustomerDetailPage() {
  const { id } = Route.useParams();
  const toast = useToastHelpers();

  const [isEditing, setIsEditing] = useState(false);
  const [showAddressModal, setShowAddressModal] = useState(false);

  const { data: customer, isLoading, error } = useCustomer(id);
  const { data: addresses } = useCustomerAddresses(id);

  const updateCustomer = useUpdateCustomer();
  const archiveCustomer = useArchiveCustomer();
  const createAddress = useCreateAddress(id);
  const deleteAddress = useDeleteAddress(id);

  if (isLoading) {
    return (
      <ProtectedRoute>
        <AppShell>
          <div className="flex justify-center py-12">
            <LoadingSpinner size="lg" />
          </div>
        </AppShell>
      </ProtectedRoute>
    );
  }

  if (error || !customer) {
    return (
      <ProtectedRoute>
        <AppShell>
          <GlassPanel>
            <div className="text-center py-8">
              <p className="text-red-400">Kunde nicht gefunden</p>
              <Link to="/customers" className="text-white/60 hover:text-white mt-2 inline-block">
                Zurück zur Übersicht
              </Link>
            </div>
          </GlassPanel>
        </AppShell>
      </ProtectedRoute>
    );
  }

  const handleUpdate = async (data: { version?: number } & Record<string, unknown>) => {
    try {
      await updateCustomer.mutateAsync({ id, ...data } as Parameters<
        typeof updateCustomer.mutate
      >[0]);
      markFormSaved();
      toast.success('Kunde gespeichert');
      setIsEditing(false);
    } catch (error) {
      toast.error(apiErrorMessage(error));
    }
  };

  const handleArchive = async () => {
    try {
      await archiveCustomer.mutateAsync({
        id,
        isArchived: !customer.isArchived,
        version: customer.version,
      });
      toast.success(customer.isArchived ? 'Kunde wiederhergestellt' : 'Kunde archiviert');
    } catch {
      toast.error('Fehler beim Archivieren');
    }
  };

  const handleAddAddress = async (data: Parameters<typeof createAddress.mutate>[0]) => {
    try {
      await createAddress.mutateAsync(data);
      toast.success('Adresse hinzugefügt');
      setShowAddressModal(false);
    } catch {
      toast.error('Fehler beim Hinzufügen');
    }
  };

  const handleDeleteAddress = async (addressId: string) => {
    if (!confirm('Adresse wirklich löschen?')) return;
    try {
      await deleteAddress.mutateAsync(addressId);
      toast.success('Adresse gelöscht');
    } catch {
      toast.error('Fehler beim Löschen');
    }
  };

  const contactName = [customer.contactFirstName, customer.contactLastName]
    .filter(Boolean)
    .join(' ');

  return (
    <ProtectedRoute>
      <AppShell>
        {/* Header — 006b P2: shared PageHeader (backTo-Eyebrow-Kanon) */}
        <PageHeader
          backTo="/customers"
          backLabel="Kunden"
          title={customer.companyName}
          titleAccessory={
            /* 006b P5: Lifecycle-Zustand → StatusBadge (Kanon §8) */
            customer.isArchived ? <StatusBadge tone="muted">Archiviert</StatusBadge> : undefined
          }
          subtitle={customer.customerNumber}
          actions={
            <div className="flex flex-wrap items-center justify-end gap-2">
              <Link to="/invoices/new" search={{ customerId: customer.id }}>
                <Button variant="secondary">Rechnung erstellen</Button>
              </Link>
              <Link to="/estimates/new" search={{ customerId: customer.id }}>
                <Button variant="secondary">Angebot erstellen</Button>
              </Link>
              <Link to="/contracts/new" search={{ customerId: customer.id }}>
                <Button variant="secondary">Vertrag erstellen</Button>
              </Link>
              <Button variant="secondary" onClick={handleArchive} disabled={isEditing}>
                {customer.isArchived ? 'Wiederherstellen' : 'Archivieren'}
              </Button>
              {!isEditing && <Button onClick={() => setIsEditing(true)}>Bearbeiten</Button>}
            </div>
          }
        />

        {isEditing ? (
          <CustomerForm
            key={customer.id}
            customer={customer}
            onSubmit={handleUpdate}
            isLoading={updateCustomer.isPending}
            onCancel={() => setIsEditing(false)}
          />
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Customer Info */}
            <GlassPanel>
              <h2 className="text-lg font-medium text-white mb-4">Kontakt</h2>
              <dl className="space-y-3">
                {contactName && (
                  <div>
                    <dt className="text-xs text-white/40 uppercase">Ansprechpartner</dt>
                    <dd className="text-white/80">{contactName}</dd>
                  </div>
                )}
                {customer.email && (
                  <div>
                    <dt className="text-xs text-white/40 uppercase">E-Mail</dt>
                    <dd className="text-white/80">
                      <a href={`mailto:${customer.email}`} className="hover:text-white">
                        {customer.email}
                      </a>
                    </dd>
                  </div>
                )}
                {customer.phone && (
                  <div>
                    <dt className="text-xs text-white/40 uppercase">Telefon</dt>
                    <dd className="text-white/80">
                      <a href={`tel:${customer.phone}`} className="hover:text-white">
                        {customer.phone}
                      </a>
                    </dd>
                  </div>
                )}
                <div>
                  <dt className="text-xs text-white/40 uppercase">Zahlungsziel</dt>
                  <dd className="text-white/80">
                    {formatPaymentTerm(customer.defaultPaymentDays)}
                  </dd>
                </div>
                {customer.notes && (
                  <div>
                    <dt className="text-xs text-white/40 uppercase">Notizen</dt>
                    <dd className="text-white/60 whitespace-pre-wrap">{customer.notes}</dd>
                  </div>
                )}
              </dl>
            </GlassPanel>

            {/* Addresses */}
            <GlassPanel>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-medium text-white">Adressen</h2>
                <Button variant="secondary" size="sm" onClick={() => setShowAddressModal(true)}>
                  Hinzufügen
                </Button>
              </div>

              {addresses && addresses.length > 0 ? (
                <div className="space-y-3">
                  {addresses.map((address) => (
                    <div
                      key={address.id}
                      className="p-3 rounded-lg bg-white/[0.02] border border-white/[0.06]"
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-xs text-white/40 uppercase">
                              {address.addressType === 'billing'
                                ? 'Rechnungsadresse'
                                : 'Lieferadresse'}
                            </span>
                            {address.isDefault && (
                              <span className="px-1.5 py-0.5 text-xs bg-white/10 text-white/60 rounded-lg">
                                Standard
                              </span>
                            )}
                          </div>
                          <p className="text-white/80">{address.street}</p>
                          {address.street2 && <p className="text-white/60">{address.street2}</p>}
                          <p className="text-white/60">
                            {address.postalCode} {address.city}
                          </p>
                          <p className="text-white/40">{address.country}</p>
                        </div>
                        <button
                          onClick={() => handleDeleteAddress(address.id)}
                          className="text-white/30 hover:text-red-400 transition-colors"
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                            />
                          </svg>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-white/40 text-sm">Keine Adressen vorhanden</p>
              )}
            </GlassPanel>
          </div>
        )}

        {/* Audit trail (Phase 13 Cluster A Feature D) */}
        <div className="mt-6">
          <EntityAuditSection entityType="customer" entityId={customer.id} />
        </div>

        {/* Address Modal */}
        <Modal
          isOpen={showAddressModal}
          onClose={() => setShowAddressModal(false)}
          title="Adresse hinzufügen"
        >
          <AddressForm
            onSubmit={handleAddAddress}
            isLoading={createAddress.isPending}
            onCancel={() => setShowAddressModal(false)}
          />
        </Modal>
      </AppShell>
    </ProtectedRoute>
  );
}
